/* eslint-disable react-hooks/rules-of-hooks */
import type { Branding } from "../../shared/types/commonTypes";
import { FC, useEffect, useLayoutEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Alert,
  Box,
  Grid,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CustomTextField from "../../components/CustomTextField";
import CustomSwitch from "../../components/CustomSwitch";
import { NotificationManager } from "../../services/api/NotificationManager";
import { GetNotificationSettingsResponse } from "../../shared/payloads/notificationSettingsPayloads";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { useDispatch } from "react-redux";
import DOMPurify from "dompurify";
import HelpButton from "../../components/HelpButton";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import CustomColorPicker from "../../components/NotificationEditComponents/CustomColorPicker";
import TextEditor from "../../components/NotificationEditComponents/TextEditor";
import { convertToRaw, EditorState, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import Tabs from "../../components/Tabs";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";
import { convertStringToLanguage } from "../../generic-utils";
import i18n from "../../i18n";

interface CustomNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  createdNotificationId: string;
  data?: GetNotificationSettingsResponse;
  branding: Branding;
}

type TabType = "rules" | "design";

const MAX_NUMBER_OF_CHARACTERS_IN_NOTIFICATION_BODY = 130;

const notificationStyleId = v4();

const CustomNotificationModal: FC<CustomNotificationModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  createdNotificationId,
  data,
  branding,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<TabType>("rules");
  const language = convertStringToLanguage(i18n.language);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notificationName, setNotificationName] = useState<string>(
    data?.name ? data?.name : "",
  );
  const [notificationText, setNotificationText] = useState<EditorState>(
    data?.text
      ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            htmlToDraft(data.text).contentBlocks,
            htmlToDraft(data.text).entityMap,
          ),
        )
      : EditorState.createEmpty(),
  );
  const textLength = notificationText.getCurrentContent().getPlainText().length;
  const isTextTooLong =
    textLength > MAX_NUMBER_OF_CHARACTERS_IN_NOTIFICATION_BODY;

  const [buttonName, setButtonName] = useState<string>(
    data?.linkText ? data?.linkText : "",
  );
  const [withLink, setWithLink] = useState<boolean>(
    data?.addLink !== undefined ? data?.addLink : true,
  );
  const [linkInNewTab, setLinkInNewTab] = useState<boolean>(
    data?.openLinkInNewWindow !== undefined ? data?.openLinkInNewWindow : true,
  );
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );
  const [link, setLink] = useState<string>(data?.link ? data?.link : "");
  const [linkAsButton, setLinkAsButton] = useState<boolean>(
    data?.linkAsButton !== undefined ? data?.linkAsButton : true,
  );

  const [notificationHtml, setNotificationHtml] = useState<string>("");

  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconBackgroundColor || null,
  );
  const [iconColor, setIconColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconColor || null,
  );
  const [linkTextColor, setLinkTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.linkTextColor || null,
  );
  const [linkButtonBackgroundColor, setLinkButtonBackgroundColor] = useState<
    string | null
  >(data?.notificationVisualSettings?.linkButtonBackgroundColor || null);
  const [borderRadius, setBorderRadius] = useState<number | null>(
    data?.notificationVisualSettings?.roundness || null,
  );
  const [icon, setIcon] = useState<string | null>(
    data?.notificationVisualSettings?.icon || null,
  );

  const [backgroundOpacity, setBackgroundOpracity] = useState<number | null>(
    null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(null);

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  const onCloseAndClearInputs = () => {
    setNotificationName("");
    setNotificationText(EditorState.createEmpty());
    setButtonName("");
    setWithLink(true);
    setLinkInNewTab(true);
    setWithRepeat(true);
    setLink("");
    setLinkAsButton(true);
    onClose();
  };

  const normalizeRawContent = (editorState: EditorState) => {
    const raw = convertToRaw(editorState.getCurrentContent());
    return {
      blocks: raw.blocks.map(({ key, ...rest }) => rest),
      entityMap: raw.entityMap,
    };
  };

  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const iconColorDefault =
      data?.notificationVisualSettings?.iconColor ??
      branding?.iconColor ??
      NotificationDefaultSettings.IconColor;

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    const defaultNotificationName = data?.name ? data?.name : "";

    const defaultLink = data?.link ? data?.link : "";

    const defaultButtonName = data?.linkText ? data?.linkText : "";

    const defaultNotificationText = data?.text
      ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            htmlToDraft(data.text).contentBlocks,
            htmlToDraft(data.text).entityMap,
          ),
        )
      : EditorState.createEmpty();

    const defaultLinkAsButton =
      data?.linkAsButton !== undefined ? data?.linkAsButton : true;

    const defaultLinkInNewTab =
      data?.openLinkInNewWindow !== undefined
        ? data?.openLinkInNewWindow
        : true;

    const defaultLinkTextColor =
      data?.notificationVisualSettings?.linkTextColor || null;

    const defaultLinkButtonBackgroundColor =
      data?.notificationVisualSettings?.linkButtonBackgroundColor || null;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (iconColor && iconColor !== iconColorDefault) {
      setDetectedChange(true);
    } else if (
      icon &&
      (data?.notificationVisualSettings?.icon
        ? icon !== data?.notificationVisualSettings?.icon
        : icon !== NotificationDefaultSettings.IndividualIcon)
    ) {
      setDetectedChange(true);
    } else if (withRepeat !== withRepeatDefault) {
      setDetectedChange(true);
    } else if (
      typeof data?.addLink === "boolean"
        ? data?.addLink !== withLink
        : withLink !== true
    ) {
      setDetectedChange(true);
    } else if (notificationName !== defaultNotificationName) {
      setDetectedChange(true);
    } else if (
      JSON.stringify(normalizeRawContent(defaultNotificationText)) !==
      JSON.stringify(normalizeRawContent(notificationText))
    ) {
      setDetectedChange(true);
    } else if (defaultLink !== link) {
      setDetectedChange(true);
    } else if (defaultButtonName !== buttonName) {
      setDetectedChange(true);
    } else if (defaultLinkAsButton !== linkAsButton) {
      setDetectedChange(true);
    } else if (defaultLinkInNewTab !== linkInNewTab) {
      setDetectedChange(true);
    } else if (defaultLinkTextColor !== linkTextColor) {
      setDetectedChange(true);
    } else if (defaultLinkButtonBackgroundColor !== linkButtonBackgroundColor) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    withRepeat,
    withLink,
    active,
    icon,
    textColor,
    iconColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    notificationName,
    notificationText,
    linkTextColor,
    linkButtonBackgroundColor,
    buttonName,
    link,
    linkAsButton,
    linkInNewTab,
    data?.notificationVisualSettings?.linkButtonBackgroundColor,
    data?.openLinkInNewWindow,
    data?.name,
    data?.linkText,
    data?.addLink,
    data?.link,
    data?.linkAsButton,
    data?.notificationVisualSettings?.linkTextColor,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
    data?.repeatNotification,
  ]);

  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpracity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setIconColor(data?.notificationVisualSettings?.iconColor ?? null);
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIcon(data?.notificationVisualSettings?.icon ?? null);
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
    setWithLink(data?.addLink ?? true);
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.icon,
    data?.addLink,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.activeSize,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundRadius,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: "individual",
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                iconColor ??
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: linkTextColor ?? "",
              linkButtonBackgroundColor: linkButtonBackgroundColor ?? "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        "individual",
        undefined,
        draftToHtml(convertToRaw(notificationText.getCurrentContent())),
        undefined,
        language,
        undefined,
        undefined,
        undefined,
        undefined,
        withLink,
        buttonName,
        linkAsButton,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    active,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    branding?.activeSize,
    branding?.backgroundColor,
    branding?.backgroundOpacity,
    branding?.borderRadius,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
    branding?.iconColor,
    branding?.textColor,
    branding?.logoDisplay,
    branding?.logo,
    branding?.logoBackgroundColor,
    branding?.logoText,
    buttonName,
    linkButtonBackgroundColor,
    linkTextColor,
    data?.notificationVisualSettings?.linkButtonBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.textColor,
    icon,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    linkAsButton,
    notificationText,
    showIcon,
    textColor,
    withLink,
  ]);

  const handleNotificationNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setNotificationName(event.target.value);
  };

  const handleButtonNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;

    if (newValue.length <= 33) setButtonName(newValue);
  };

  const handleLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLink(event.target.value);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const toggleWithLink = () => setWithLink(!withLink);

  const toggleLinkInNewTab = () => setLinkInNewTab(!linkInNewTab);

  const toggleWithRepeat = () => setWithRepeat(!withRepeat);

  const toggleLinkAsButton = () => setLinkAsButton(!linkAsButton);

  const onSave = async () => {
    if (withLink && buttonName.length === 0) {
      showFlashMessageWithTimeout(dispatch, t("popups.setButtonText"), "error");
      return;
    }

    const urlPattern = /^(https?:\/\/)/;

    if (withLink && !urlPattern.test(link)) {
      showFlashMessageWithTimeout(dispatch, t("popups.missingHttp"), "error");
      return;
    }

    try {
      setIsLoading(true);
      const _notificationManager = NotificationManager.getManager();
      const body = {
        id: createdNotificationId,
        isActive: true,
        repeatNotification: withRepeat,
        addLink: withLink,
        name: notificationName,
        text: draftToHtml(convertToRaw(notificationText.getCurrentContent())),
        linkAsButton: withLink ? linkAsButton : false,
        openLinkInNewWindow: withLink ? linkInNewTab : false,
        linkText: withLink ? buttonName : undefined,
        link: withLink ? link : undefined,

        notificationVisualSettings: {
          backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
          textColor: textColor ?? branding?.textColor ?? "",
          iconBackgroundColor:
            iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
          iconColor: iconColor ?? branding?.iconColor ?? "",
          icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
          roundness: borderRadius ?? branding?.borderRadius ?? 0,
          backgroundOpacity:
            backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
          activeSize: active ?? branding?.activeSize ?? "classic",
          isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
          iconBackgroundRadius:
            iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
          linkTextColor: linkTextColor ?? "",
          linkButtonBackgroundColor: linkButtonBackgroundColor ?? "",
        },
      };
      if (
        notificationName === "" ||
        notificationText.getCurrentContent().isEmpty()
      ) {
        showFlashMessageWithTimeout(
          dispatch,
          t("popups.addNameAndText"),
          "error",
        );
        setIsLoading(false);
        return;
      }

      await _notificationManager.updateNotificationSettingsAsync(body);
      setIsLoading(false);
      onSaveSuccess?.();
      onCloseAndClearInputs();
    } catch (e: any) {
      setIsLoading(false);
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  const onReset = async () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpracity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setIconColor(null);
    setIcon(null);
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
    setNotificationName(data?.name ? data?.name : "");
    setWithLink(data?.addLink !== undefined ? data?.addLink : true);
    setNotificationText(
      data?.text
        ? EditorState.createWithContent(
            ContentState.createFromBlockArray(
              htmlToDraft(data.text).contentBlocks,
              htmlToDraft(data.text).entityMap,
            ),
          )
        : EditorState.createEmpty(),
    );
    setLink(data?.link ? data?.link : "");
    setButtonName(data?.linkText ? data?.linkText : "");
    setLinkAsButton(
      data?.linkAsButton !== undefined ? data?.linkAsButton : true,
    );
    setLinkInNewTab(
      data?.openLinkInNewWindow !== undefined
        ? data?.openLinkInNewWindow
        : true,
    );
    setLinkTextColor(data?.notificationVisualSettings?.linkTextColor || null);
    setLinkButtonBackgroundColor(
      data?.notificationVisualSettings?.linkButtonBackgroundColor || null,
    );
  };

  const tabs = [
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: (
        <>
          <div
            style={{
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1),
            }}
          >
            <HelpButton link='https://www.overenyweb.cz/napoveda/nastaveni-notifikaci/vlastni-notifikace/' />
          </div>
          <Grid container columnSpacing={2}>
            <Grid item xs={12} md={6}>
              <Stack>
                <Typography
                  className={classes.sectionTitle}
                  style={{ marginBottom: 16 }}
                >
                  {t("campaign.notifications.settings.displayRules.title")}
                </Typography>
                <CustomTextField
                  fullWidth
                  label={
                    t(
                      "campaign.notifications.settings.displayRules.nameNotification",
                    ) ?? ""
                  }
                  value={notificationName}
                  onChange={handleNotificationNameChange}
                />
                <Box className={classes.repeatSwitchContainer}>
                  <Typography className={classes.switchLabel}>
                    {t(
                      "campaign.notifications.settings.displayRules.repeatNotification",
                    )}
                  </Typography>
                  <CustomSwitch
                    checked={withRepeat}
                    onSwitch={() => toggleWithRepeat()}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography className={classes.sectionTitle}>
                    {t("campaign.notifications.settings.displayRules.text")}
                  </Typography>
                  <Typography color={isTextTooLong ? "red" : undefined}>
                    {`${textLength}/${MAX_NUMBER_OF_CHARACTERS_IN_NOTIFICATION_BODY}`}
                  </Typography>
                </Stack>
                <TextEditor
                  value={notificationText}
                  onChange={setNotificationText}
                />
                {isTextTooLong ? (
                  <Alert severity='error'>
                    {t(
                      "campaign.notifications.settings.displayRules.individual.tooLongTextErrorMessage",
                    )}
                  </Alert>
                ) : null}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction='row' alignItems='center' mb={1}>
                <Typography
                  className={classes.sectionTitle}
                  style={{ marginRight: 24 }}
                >
                  {t(
                    "campaign.notifications.settings.displayRules.individual.link",
                  )}
                </Typography>
                <CustomSwitch checked={withLink} onSwitch={toggleWithLink} />
              </Stack>
              {!withLink && (
                <Typography
                  style={{ fontSize: 14, color: theme.palette.natural.t1 }}
                >
                  {t(
                    "campaign.notifications.settings.displayRules.individual.removedLinkDescription",
                  )}
                </Typography>
              )}
              {withLink && (
                <>
                  <CustomTextField
                    fullWidth
                    label={`${t(
                      "campaign.notifications.settings.displayRules.individual.buttonName",
                    )} ${
                      linkAsButton
                        ? t(
                            "campaign.notifications.settings.displayRules.individual.button",
                          )
                        : t(
                            "campaign.notifications.settings.displayRules.individual.link",
                          )
                    }`}
                    value={buttonName}
                    onChange={handleButtonNameChange}
                    style={{ marginBottom: 8 }}
                  />
                  <Tooltip
                    title={
                      t(
                        "campaign.notifications.settings.displayRules.individual.tooltip",
                      ) ?? ""
                    }
                  >
                    <div>
                      <CustomTextField
                        fullWidth
                        label={
                          t(
                            "campaign.notifications.settings.displayRules.individual.url",
                          ) ?? ""
                        }
                        value={link}
                        onChange={handleLinkChange}
                        style={{ marginBottom: 4 }}
                      />
                    </div>
                  </Tooltip>
                </>
              )}
              {withLink && (
                <>
                  <Box className={classes.switchContainer}>
                    <Typography className={classes.titleText}>
                      {t(
                        "campaign.notifications.settings.displayRules.individual.buttonAsLink",
                      )}
                    </Typography>
                    <CustomSwitch
                      checked={linkAsButton}
                      onSwitch={toggleLinkAsButton}
                    />
                  </Box>
                </>
              )}
              {!withLink && (
                <>
                  <Box className={classes.divider} />
                  <Box className={classes.repeatSwitchContainer}>
                    <Typography className={classes.switchLabel}>
                      {t(
                        "campaign.notifications.settings.displayRules.repeatNotification",
                      )}
                    </Typography>
                    <CustomSwitch
                      checked={withRepeat}
                      onSwitch={() => toggleWithRepeat()}
                    />
                  </Box>
                </>
              )}
            </Grid>
            <Grid item xs={12} md={6} mt={5.5}>
              {withLink && (
                <>
                  <Box className={classes.switchContainer}>
                    <Typography className={classes.titleText}>
                      {t(
                        "campaign.notifications.settings.displayRules.individual.openLinkInNewWindow",
                      )}
                    </Typography>
                    <CustomSwitch
                      checked={linkInNewTab}
                      onSwitch={toggleLinkInNewTab}
                    />
                  </Box>
                </>
              )}
              {linkAsButton && (
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  mb={1}
                  mt={0.5}
                >
                  <Typography className={classes.titleText} width={150}>
                    {t(
                      "campaign.notifications.settings.displayRules.individual.buttonColor",
                    )}
                  </Typography>
                  <CustomColorPicker
                    color={linkButtonBackgroundColor}
                    onChange={setLinkButtonBackgroundColor}
                    defaultColor={
                      NotificationDefaultSettings.ButtonBackgroundColor
                    }
                  />
                </Stack>
              )}
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          defaultIcon={NotificationDefaultSettings.IndividualIcon}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpracity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon={false}
          icon={icon}
          iconColor={iconColor}
          setIcon={setIcon}
          setIconColor={setIconColor}
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
      title={t("campaign.notifications.individual.title")}
    >
      <Tabs onTabChange={handleChange} selectedTab={selectedTab} tabs={tabs} />
    </NotificationModalBase>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tabsDivider: {
    width: 262,
    height: 3,
    backgroundColor: theme.palette.natural.t3,
    position: "absolute",
    bottom: 0,
  },
  tab: {
    textTransform: "none",
    fontSize: 16,
  },
  container: {
    flex: 1,
    width: 906,
    backgroundColor: theme.palette.natural.t4,
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 24,
    paddingBottom: 32,
    borderRadius: 8,
    boxShadow: theme.palette.main.shadow.secondary,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  titleContainer: {
    height: 34,
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: theme.palette.main.secondary.u1,
  },
  sectionTitle: {
    fontSize: 18,
    color: theme.palette.main.primary.o,
    fontWeight: "bold",
  },
  switchLabel: {
    color: theme.palette.natural.t1,
    fontSize: 16,
  },
  optionsContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
  },
  buttonsContainer: {
    height: 44,
    flex: 1,
    display: "flex",
    flexDirection: "row-reverse",
  },
  titleSwitchContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 42,
    marginBottom: 16,
  },
  switchContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleText: {
    fontSize: 16,
    color: theme.palette.natural.t1,
  },
  divider: {
    height: 1,
    flex: 1,
    marginTop: 24,
    borderTop: `3px dashed ${theme.palette.main.secondary.d1}`,
  },
  repeatSwitchContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
}));

export default CustomNotificationModal;
